import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WalsallCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Walsall Pothole Claims | 26% U-Roads RED, RED Best Practice | Fixtyer"
        description="Walsall Council earns GREEN on spend but RED on DfT best practice — with 26% of 697km unclassified roads in RED condition, just 12% preventative spend projected for 2025/26, and 27,025 pothole fills in five years. See what the AMBER rating means for your claim."
        canonical="/councils/walsall"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Walsall', url: '/councils/walsall' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Walsall spends more than its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spending. Walsall\'s overall rating is AMBER because condition is AMBER and best practice is RED: preventative maintenance is projected at just 12% in 2025/26, unclassified-road RED condition has risen from 20% to 26% since 2020, and the council estimates up to 5,000 potholes may need attention this year alone.',
          },
          {
            question: 'What does Walsall\'s RED best practice rating mean for my claim?',
            answer: 'The DfT best practice scorecard and Walsall\'s published spending figures are contextual information about network-level maintenance. They do not establish liability, notice of a specific defect, or whether the inspection and repair response for your road was reasonable.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 697km — 82% of Walsall\'s 847km network. CVI surveys cover 25% of the unclassified network each year over a four-year cycle, so any given residential street may only receive a published condition survey once every four years. At the 2024 survey, 26% of U-roads were in RED condition — roughly 181km of estate and residential routes where the council\'s own data says maintenance should be considered.',
          },
          {
            question: 'Walsall\'s classified roads look fine — does that weaken my claim?',
            answer: 'Classified-road figures do not determine a claim involving an unclassified road. Walsall\'s A-roads improved from 2.1% RED in 2020 to 1.5% in 2024, and B/C roads held at 1.1% RED, while U-road RED condition rose from 20% to 26%. Those network-level figures provide context; records for the specific defect and location remain central.',
          },
          {
            question: 'Does the council\'s Velocity Patch programme help my claim?',
            answer: 'The Velocity Patch programme is contextual information about one repair method. It does not show what Walsall knew about a specific defect or whether the inspection and repair response at that location was reasonable.',
          },
          {
            question: 'Walsall filled 5,122 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'Repair totals do not establish the condition of a specific road or defect. The council filled 5,122 potholes in 2024/25 and estimates up to 5,000 may need attention in 2025/26, while U-road RED condition reached 26%. These network-level figures are contextual information only.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Walsall Council"
        councilSlug="walsall"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/walsall"
        headline="Walsall Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Walsall</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Walsall: one in four residential roads in RED condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Walsall Council outspends its DfT capital allocation and earns a GREEN spend scorecard.
            Yet <strong className="text-rose-400">26% of its 697km unclassified network</strong> is in
            RED condition — up from 20% in 2020, every year without exception — and the DfT rates its
            best practice <strong className="text-rose-400">RED</strong> with just{' '}
            <strong className="text-rose-400">12% preventative spend</strong> projected for 2025/26.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">26%</div>
            <div className="text-white font-medium mt-2">Unclassified roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Rising every year since 2020 on 697km of residential streets — 82% of Walsall&apos;s
              847km carriageway network. Roughly 181km where the council&apos;s own CVI data says
              maintenance should be considered.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The DfT&apos;s verdict — GREEN spend, RED best practice
          </h2>
          <p className="text-surface-600 mb-8">
            Official 2025/26 scorecards from the Department for Transport, cross-referenced with
            Walsall&apos;s own transparency report (published 12 May 2025)
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Walsall Council DfT Road Maintenance Ratings 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Metric</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">What the data shows</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center text-surface-600">AMBER condition + RED best practice offset GREEN spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Road condition</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-800">U-roads 26% RED — rising five years straight</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center text-emerald-800">£7.909m capital vs £6.606m DfT allocation (2025/26)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Best practice</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-700">12% preventative · 28% reactive (2025/26 proj.)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <p className="text-emerald-800 text-sm">
                <strong>What GREEN spend means:</strong> Walsall&apos;s projected 2025/26 capital spend of
                £7.909m exceeds its £6.606m DfT allocation — a pattern repeated across every published year.
                The council secured an additional £5.2 million through West Midlands and DfT Challenge Fund
                initiatives for heavily trafficked classified roads. Money is not the headline problem.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <p className="text-rose-800 text-sm">
                <strong>What RED best practice means:</strong> The DfT penalises councils whose own published
                figures show reactive maintenance outpacing prevention. Walsall&apos;s preventative share has
                fallen from 17% in 2023/24 to a projected 12% in 2025/26 — while it estimates up to 5,000
                potholes may need attention this financial year. That split is why best practice is RED despite
                GREEN spend.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RED Best Practice */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why best practice is RED
          </h2>
          <p className="text-surface-600 mb-8">
            The DfT&apos;s weakest scorecard for Walsall — explained by the council&apos;s own spending table
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative*</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive**</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£5.673m</td>
                  <td className="px-4 py-3 text-center">£8.473m</td>
                  <td className="px-4 py-3 text-center">£2.026m</td>
                  <td className="px-4 py-3 text-center">12%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£2.767m</td>
                  <td className="px-4 py-3 text-center">£5.567m</td>
                  <td className="px-4 py-3 text-center">£1.713m</td>
                  <td className="px-4 py-3 text-center">11%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£3.330m</td>
                  <td className="px-4 py-3 text-center">£5.933m</td>
                  <td className="px-4 py-3 text-center">£2.094m</td>
                  <td className="px-4 py-3 text-center">17%</td>
                  <td className="px-4 py-3 text-center">32%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£4.250m</td>
                  <td className="px-4 py-3 text-center">£7.553m</td>
                  <td className="px-4 py-3 text-center">£2.272m</td>
                  <td className="px-4 py-3 text-center">17%</td>
                  <td className="px-4 py-3 text-center">31%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£3.713m</td>
                  <td className="px-4 py-3 text-center">£7.452m</td>
                  <td className="px-4 py-3 text-center">£2.348m</td>
                  <td className="px-4 py-3 text-center">16%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26</td>
                  <td className="px-4 py-3 text-center">£6.606m</td>
                  <td className="px-4 py-3 text-center">£7.909m</td>
                  <td className="px-4 py-3 text-center">£2.506m</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">12%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">28%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-xs mb-8">
            * Estimate of percentage spent on preventative maintenance. ** Estimate of percentage spent on
            reactive maintenance. Source: Walsall Council Local Highways Maintenance Transparency Report,
            published 12 May 2025.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">The reactive tilt</h3>
              <p className="text-rose-800 text-sm">
                Preventative spend peaked at 17% in 2022/23 and 2023/24, then fell to 16% in 2024/25 and
                a projected 12% in 2025/26 — the lowest figure in six published years. Reactive maintenance
                has sat at 28–32% throughout. That is the patch-first pattern the DfT&apos;s best practice
                scorecard is designed to catch.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">GREEN spend, different deployment</h3>
              <p className="text-emerald-800 text-sm">
                Total capital spend exceeded the DfT allocation in every published year — reaching £8.473m
                against £5.673m allocated in 2020/21. The council is investing. The question is whether
                investment reaches the 697km of residential roads where RED condition is climbing — or
                concentrates on the 150km of classified carriageway where SCANNER data already looks strong.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Prevention is better than cure — intervening at the right time will reduce the number of potholes forming and prevent bigger problems later. Right first time — do it once and get it right, rather than face continuous bills."
            source="Walsall Council Local Highways Maintenance Transparency Report 2025 — citing HMEP 'Prevention and a Better Cure'"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI survey data from Walsall&apos;s own transparency report — classified
            roads stable, residential network deteriorating
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">The network (847km total roads)</h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">98km</div>
                <div className="text-sm text-surface-600">A roads (11.6%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">52km</div>
                <div className="text-sm text-surface-600">B and C roads (6.1%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">697km</div>
                <div className="text-sm text-rose-800">U roads (82.3%)</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Walsall also maintains 1,312km of footways, 99km of other public rights of way and 19km of
              cycleways. Motorways and trunk roads including the M6 and A5 are National Highways&apos; responsibility.
            </p>
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (98km): improving</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2.1%</td>
                    <td className="px-4 py-2 text-center">17.2%</td>
                    <td className="px-4 py-2 text-center">80.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.9%</td>
                    <td className="px-4 py-2 text-center">17.2%</td>
                    <td className="px-4 py-2 text-center">80.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.3%</td>
                    <td className="px-4 py-2 text-center">19.1%</td>
                    <td className="px-4 py-2 text-center">78.6%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.1%</td>
                    <td className="px-4 py-2 text-center">18.4%</td>
                    <td className="px-4 py-2 text-center">79.5%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">1.5%</td>
                    <td className="px-4 py-2 text-center font-bold">16.8%</td>
                    <td className="px-4 py-2 text-center font-bold">81.7%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              SCANNER surveys cover 100% of the A-road network in both directions over a two-year period.
              Credit where due: principal roads have genuinely improved. But A-roads are just one-ninth of
              the network.
            </p>
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (52km): stable</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.1%</td>
                    <td className="px-4 py-2 text-center">15.5%</td>
                    <td className="px-4 py-2 text-center">83.3%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.7%</td>
                    <td className="px-4 py-2 text-center">17.4%</td>
                    <td className="px-4 py-2 text-center">80.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.6%</td>
                    <td className="px-4 py-2 text-center">19.1%</td>
                    <td className="px-4 py-2 text-center">79.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1.1%</td>
                    <td className="px-4 py-2 text-center">16.8%</td>
                    <td className="px-4 py-2 text-center">82.1%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">1.1%</td>
                    <td className="px-4 py-2 text-center font-bold">16.8%</td>
                    <td className="px-4 py-2 text-center font-bold">82.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B and C roads have held steady — partly reflecting £5.2 million of Challenge Fund investment
              on heavily trafficked routes. SCANNER surveys cover 100% of the B and C network in both
              directions over a two-year period.
            </p>
          </div>

          <QuoteCard
            quote="The condition of Walsall Council's classified road network has benefited significantly from the injection of additional funding over recent years because of two successful West Midlands and Department for Transport (DfT) Challenge Fund investment initiatives which secured an additional £5.2 million of government grant funding to improve the condition of many of the region's most heavily trafficked roads."
            source="Walsall Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 697km four-year blind spot
          </h2>
          <p className="text-surface-600 mb-8">
            82% of the network on a rolling CVI survey — RED condition rising every year since 2020
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">25%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The four-year CVI cycle</h3>
              <p className="text-surface-600 text-sm mb-3">
                Walsall&apos;s transparency report states CVI surveys are carried out annually across 100%
                of the unclassified network over a four-year period — meaning 25% is surveyed each year.
                Unlike SCANNER on classified roads, CVI produces only a RED category for U-roads, with no
                amber band for roads approaching failure.
              </p>
              <p className="text-surface-600 text-sm">
                At 26% RED in 2024, roughly <strong>181km of residential streets</strong> should be
                considered for maintenance — on a network where your road may not have received a published
                condition survey for up to three years.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS 2161 from 2026/27</h3>
              <p className="text-amber-800 text-sm mb-3">
                From 2026/27, local highway authorities must use suppliers accredited against BSI PAS 2161,
                expanding condition categories from three to five. Walsall&apos;s published report notes this
                change explicitly.
              </p>
              <p className="text-amber-800 text-sm">
                Until then, the 20% → 26% RED trend on U-roads — measured under the current CVI methodology
                — is the network-level evidence your claim can cite for residential-road incidents.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Walsall must show it had a reasonable system for knowing
              the condition of its roads. For the unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last CVI condition-surveyed — and was it in the 25% surveyed that year?</li>
              <li>• If 26% of U-roads were RED at the 2024 survey, what was done about yours?</li>
              <li>• How does a four-year survey rotation catch defects that form between inspections?</li>
              <li>• Why is preventative spend projected at 12% while U-road RED condition keeps climbing?</li>
            </ul>
            <p className="text-white font-medium">
              The RED best-practice rating and four-year condition-survey rotation are relevant questions
              to explore, but neither proves liability nor automatically defeats a Section 58 defence.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            27,025 potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated repair counts from Walsall&apos;s transparency report — a reactive maintenance programme
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8,286</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">4,157</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">4,480</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">4,980</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">5,122</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">27,025</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~15 potholes a day, every day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Walsall fills around 15 potholes per day. The council estimates
                up to 5,000 may need attention in 2025/26 depending on winter severity — while planning
                to resurface around six miles of carriageway and surface-treat around seven miles. Reactive
                volume outpaces planned structural renewal.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Velocity Patch at scale</h3>
              <p className="text-amber-800 text-sm">
                Walsall brings in a specialist Velocity Patch repair contractor before and after the most
                severe winter weather each year. Engineers believe it offers the fastest, most durable
                solution for meeting prevailing best-practice guidance — a reactive technology deployed
                when frost and ice drive defect numbers up, not a substitute for resurfacing deteriorating
                U-roads.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Depending on the severity of weather conditions, it is estimated that up to 5,000 potholes could need attention during 2025/26."
            source="Walsall Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Walsall plans for 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            Provisional maintenance programmes from the council&apos;s own transparency report
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">~6 miles</div>
              <div className="text-sm text-surface-600 mt-1">Carriageway resurfacing</div>
              <div className="text-xs text-surface-500 mt-1">£3.6m investment</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">~7 miles</div>
              <div className="text-sm text-surface-600 mt-1">Surface treatment</div>
              <div className="text-xs text-surface-500 mt-1">£1.0m investment</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">~2 miles</div>
              <div className="text-sm text-surface-600 mt-1">Footway reconstruction</div>
              <div className="text-xs text-surface-500 mt-1">£0.6m investment</div>
            </div>
            <div className="bg-rose-50 rounded-xl p-6 border border-rose-200 text-center">
              <div className="text-3xl font-bold text-rose-700">Up to 5,000</div>
              <div className="text-sm text-rose-800 mt-1">Potholes estimated</div>
              <div className="text-xs text-rose-600 mt-1">Weather-dependent</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Structured safety inspections</h3>
              <p className="text-surface-600 text-sm">
                Walsall undertakes structured highway safety inspections across the borough to identify
                defects including potholes. Streets suffering large numbers of potholes are analysed within
                the council&apos;s methodologies for structural or preventative maintenance programmes where
                risk applies — but the published U-road condition trend suggests reactive intervention is
                not reversing network-level deterioration.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Nine major structure projects</h3>
              <p className="text-surface-600 text-sm">
                The council aims to deliver nine major highway structure projects in 2025/26, investing
                around £0.8 million. Asset management planning follows the Well Managed Highway
                Infrastructure Code of Practice, with HAMP, HAMS and HMMP documents reviewed annually
                and published on the council website.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a GREEN-spend, RED-practice council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Walsall invests well on classified roads — but your claim may turn on the other 82%
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital exceeds DfT allocation in every published year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition improving — RED down from 2.1% to 1.5% since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £5.2m Challenge Fund secured for heavily trafficked classified routes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published asset management framework aligned to Well Managed Highway Infrastructure
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A-roads with SCANNER data. Generic claims will
                struggle on principal routes.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best practice — preventative spend projected at just 12% in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 697km U-road network on a four-year CVI survey cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 26% of U-roads in RED — up every year since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 27,025 pothole fills in five years on a predominantly reactive programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Up to 5,000 potholes estimated for 2025/26 alone
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Focus on the specific evidence</h3>
            <p className="text-brand-200 mb-4">
              The GREEN spend and RED best-practice ratings are background context. An individual assessment
              should focus on the <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online reports) — potential evidence of notice, subject to the report details</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class and how the four-year CVI survey cycle applied to the specific location</li>
              <li>• Whether the council&apos;s own U-road RED trend supports an argument that your street was in a deteriorating network</li>
            </ul>
            <p className="text-white font-medium">
              Mac can search for prior reports, assess photo evidence, and include Walsall&apos;s published
              transparency data as context. You review the material and decide what is relevant to your claim.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Walsall first
          </h2>
          <p className="text-surface-600 mb-6">
            Walsall&apos;s transparency report covers reactive maintenance undertaken in line with Section 41
            and Section 58 statutory undertakings. Reporting the defect through the council creates a dated
            record — useful evidence if the pothole was reported before your incident, or if the council failed
            to repair it within a reasonable time.
          </p>
          <a
            href="https://go.walsall.gov.uk/apply/myaccount-online-services/roads-pavements-signs-and-lights/report-pothole"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Walsall Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the council&apos;s online form via MyAccount. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Walsall?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED-rated best-practice council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year U-road survey argument</li>
                <li>• No RED best practice citation</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ RED best practice and 12% preventative spend cited</li>
                <li>✅ Four-year U-road CVI survey gap argued</li>
                <li>✅ 26% U-road RED trend and 27,025 fills documented</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Walsall</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=walsall"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Walsall claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Walsall spends more than its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect
                that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on
                aggregate spending. Walsall&apos;s overall rating is AMBER because condition is AMBER and
                best practice is RED: preventative maintenance is projected at just 12% in 2025/26, U-road
                RED condition has risen from 20% to 26% since 2020, and the council estimates up to 5,000
                potholes may need attention this year alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does Walsall&apos;s RED best practice rating mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT best practice scorecard and Walsall&apos;s published spending figures are contextual
                information about network-level maintenance. They do not establish liability, notice of a
                specific defect, or whether the inspection and repair response for your road was reasonable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 697km — 82% of Walsall&apos;s 847km network. CVI surveys cover 25% of the
                unclassified network each year over a four-year cycle, so any given residential street may
                only receive a published condition survey once every four years. At the 2024 survey, 26% of
                U-roads were in RED condition — roughly 181km of estate and residential routes where the
                council&apos;s own data says maintenance should be considered.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Walsall&apos;s classified roads look fine — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Classified-road figures do not determine a claim involving an unclassified road. Walsall&apos;s
                A-roads improved from 2.1% RED in 2020 to 1.5% in 2024, and B/C roads held at 1.1% RED, while
                U-road RED condition rose from 20% to 26%. Those network-level figures provide context; records
                for the specific defect and location remain central.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council&apos;s Velocity Patch programme help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The Velocity Patch programme is contextual information about one repair method. It does not show
                what Walsall knew about a specific defect or whether the inspection and repair response at that
                location was reasonable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Walsall filled 5,122 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                Repair totals do not establish the condition of a specific road or defect. The council filled
                5,122 potholes in 2024/25 and estimates up to 5,000 may need attention in 2025/26, while U-road
                RED condition reached 26%. These network-level figures are contextual information only.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://go.walsall.gov.uk/roads-parking-and-travel/roadworks-and-road-maintenance/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Walsall Council Local Highways Maintenance Transparency Report (12 May 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
